import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { makeStyles, Typography, RACCOLOR } from '@rentacenter/racstrap';
import CircularProgress from '@material-ui/core/CircularProgress';

interface DropdownOption {
  label: string;
  value: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  onChange?: (value: string) => void;
  defaultValue: string;
  loading?: boolean;
  inputLabel: string;
  isDisabled?: boolean;
  required?: boolean;
}

const useStyles = makeStyles((theme: any) => ({
  dropdown: {
    position: 'relative',
    cursor: 'pointer',
    border: '1px solid #c4c4c4',
    borderRadius: theme.typography.pxToRem(6),
    padding: theme.typography.pxToRem(7),
    background: 'white',
    fontFamily: 'OpenSans-regular',
    fontSize: theme.typography.pxToRem(14),
    marginTop: theme.typography.pxToRem(8),
  },
  selected: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: RACCOLOR.WASHED_BLACK,
  },
  selectedText: {
    fontWeight: 400,
  },
  arrow: {
    marginLeft: theme.typography.pxToRem(8),
  },
  options: {
    position: 'fixed',
    background: RACCOLOR.WHITE,
    border: '1px solid #c4c4c4',
    borderRadius: theme.typography.pxToRem(4),
    zIndex: 2147483647,
    listStyleType: 'none',
    maxHeight: '150px',
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    pointerEvents: 'auto',
  },
  option: {
    padding: theme.typography.pxToRem(10),
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  disabled: {
    backgroundColor: 'rgb(235, 235, 228)',
    cursor: 'default',
  },
  formLabel: {
    fontFamily: 'OpenSans-semibold',
    color: RACCOLOR.WASHED_BLACK,
    fontSize: theme.typography.pxToRem(14),
  },
  selectLabel: {
    backgroundColor: '#f0f0f0',
  },
  loading: {
    color: theme.palette.secondary.main,
    width: `${theme.typography.pxToRem(15)} !important`,
    height: `${theme.typography.pxToRem(15)} !important`,
    margin: '0.5px 0.5px 0 0',
  },
  reqField: {
    color: 'red',
  },
  noneOption: {
    fontStyle: 'italic',
    color: '#999',
    cursor: 'not-allowed',
  },
}));

export const CustomDropdown: React.FC<DropdownProps> = ({
  options,
  onChange,
  defaultValue,
  loading = false,
  inputLabel,
  isDisabled = false,
  required = false,
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  const handleClick = () => {
    if (!isDisabled && !loading) {
      setIsOpen((prev) => !prev);
    }
  };

  const searchHandler = (value: string) => {
    onChange?.(value);
    setIsOpen(false);
  };

  useLayoutEffect(() => {
    const update = () => {
      const rect = dropdownRef.current?.getBoundingClientRect();
      if (rect) {
        setMenuPos({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      update();
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideField = dropdownRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideField && !insideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, []);

  const selectedLabel =
    options.find((opt) => opt.value === defaultValue)?.label || 'Select';

  return (
    <>
      <Typography
        variant="body2"
        component="label"
        className={classes.formLabel}
      >
        {inputLabel}
        {required && <span className={classes.reqField}>*</span>}
      </Typography>

      <div
        className={`${classes.dropdown} ${
          isDisabled || loading ? classes.disabled : ''
        }`}
        ref={dropdownRef}
      >
        <div className={classes.selected} onClick={handleClick}>
          <span className={classes.selectedText}>{selectedLabel}</span>
          {loading ? (
            <CircularProgress className={classes.loading} />
          ) : (
            <span className={classes.arrow}>
              <svg
                fill="#000000"
                height="9px"
                width="10px"
                viewBox="0 0 407.437 407.437"
              >
                <polygon points="386.258,91.567 203.718,273.512 21.179,91.567 0,112.815 203.718,315.87 407.437,112.815" />
              </svg>
            </span>
          )}
        </div>
      </div>

      {isOpen &&
        menuPos &&
        ReactDOM.createPortal(
          <ul
            ref={menuRef}
            className={classes.options}
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {options
              .filter((option) => option.value !== '')
              .map((option) => (
                <li
                  key={option.value}
                  onClick={() => searchHandler(option.value)}
                  className={`${classes.option} ${
                    option.value === defaultValue ? classes.selectLabel : ''
                  }`}
                >
                  {option.label}
                </li>
              ))}
          </ul>,
          document.body
        )}
    </>
  );
};
